import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, CalendarClock, Play, TrendingUp } from "lucide-react";
import { useApi } from "../hooks/useApi";
import GooseBookPanel, { detailText, useGooseBook } from "../components/GooseBookPanel";
import {
  BreakdownCard, Bars, CumChart, DA_DEFAULT_PARAMS, DailyList, FeeControls, MODEL_LABEL, PolicyControls, SectionCard,
  SkAccuracyCard, SplitSummary, TIME_LABEL, Th, VolumeAndDeferrals, daQueryPairs, fmtInt, fmtNum, fmtPct, fmtSigned, signColor, defaultDates, tones,
} from "../components/BacktestKit";

// ---------------------------------------------------------------------------
// Day Ahead Backtesting — combined day-ahead + balancing-market book.
//
// The day-ahead side is the IDA1-Goose strategy (BASE → Fixing I → Fixing II → IDA1) for the
// Goose settings chosen on the page; its results are cached per day on the server and Run computes
// only the days that are missing (components/GooseBookPanel.js). On top of it the page drives a
// volume-management policy: an exit may be DEFERRED to a later venue when the walk-forward SK
// forecast available at that decision time agrees with the position
//   Fixing II (11:40) → IDA1 (14:40) → IDA2 (15:30…21:40) → IDA3 (09:40 D) → balancing (CEN).
// Shorts want surplus (negative SK), longs want shortage (positive SK).
// All money is PLN. See DAYAHEAD_BACKTESTING_INSTRUCTIONS.md.
// ---------------------------------------------------------------------------

export const DEFAULT_PARAMS = DA_DEFAULT_PARAMS;

const DayAheadBacktesting = ({ darkMode: dark }) => {
  const { fetchGet, fetchPost, isAuthenticated } = useApi();
  const t = tones(dark);

  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const goose = useGooseBook(meta?.goose, fetchPost, start, end);
  const [result, setResult] = useState(null);
  const [runQuery, setRunQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const setParam = (key, value) => setParams((p) => ({ ...p, [key]: value }));
  const numOrEmpty = (e) => (e.target.value === "" ? "" : Number(e.target.value));

  const queryString = useCallback(
    (extra = {}) => {
      const qp = new URLSearchParams(daQueryPairs(params));
      if (start) qp.set("start", start);
      if (end) qp.set("end", end);
      Object.entries(extra).forEach(([k, v]) => qp.set(k, String(v)));
      return qp.toString();
    },
    [params, start, end]
  );

  // ---- meta ----
  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetchGet("/dayahead/meta");
        const data = await res.json();
        if (!res.ok || data.status !== "success") throw new Error(data.detail || "Failed to load the day-ahead data bundle.");
        if (!alive) return;
        setMeta(data);
        setParams({ ...DEFAULT_PARAMS, ...(data.defaults || {}) });
        const dates = defaultDates(data.first_day);
        setStart(dates.start);
        setEnd(dates.end);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load the day-ahead data bundle.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [isAuthenticated, fetchGet]);

  // ---- run: make sure the Goose results of the dates are cached (computing what is missing), then simulate ----
  const runBacktest = useCallback(async () => {
    if (!meta) return;
    setLoading(true);
    setError(null);
    try {
      const key = await goose.ensure();
      const qs = queryString({ goose: key });
      const res = await fetchGet(`/dayahead/run?${qs}`);
      const data = await res.json();
      if (!res.ok || data.status !== "success") throw new Error(detailText(data.detail, "run failed"));
      setResult(data);
      setRunQuery(qs);
    } catch (e) {
      setError(e.message || "Backtest failed.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [meta, goose, fetchGet, queryString]);

  // the drill-down uses exactly the parameters of the run on screen
  const fetchDay = useCallback(
    async (day) => {
      const res = await fetchGet(`/dayahead/day?${runQuery}&day=${day}`);
      const data = await res.json();
      return res.ok && data.status === "success" ? data : { hours: [] };
    },
    [fetchGet, runQuery]
  );

  const summary = result?.summary;
  const byDay = useMemo(() => result?.by_day || [], [result]);
  const byMonth = result?.by_month || [];
  const resultBook = result?.goose_book;
  const i2Label = TIME_LABEL[result?.params?.i2_time || params.i2_time] || params.i2_time;
  const sameGoose = resultBook && goose.status?.key === resultBook.key;

  // -------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      <div>
        <h1 className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-800"}`}>Day Ahead Backtesting</h1>
        <p className={`text-xs mt-1 ${t.faint}`}>
          IDA1-Goose day-ahead book (BASE → Fixing I → Fixing II → IDA1) with forecast-driven volume management to IDA2, IDA3 and the
          balancing market.
          {meta ? ` Data ${meta.first_day} → ${meta.last_day} (${meta.days} days); dates after ${meta.last_day} are not settled in the data yet.` : ""}
        </p>
      </div>

      {/* ---------------- Configuration ---------------- */}
      <SectionCard title="Configuration" icon={<Activity className="w-4 h-4" />} dark={dark}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label className={t.labelCls}>Start Date</label>
            <input type="date" className={t.inputCls} value={start} min={meta?.first_day} max={meta?.last_day} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className={t.labelCls}>End Date</label>
            <input type="date" className={t.inputCls} value={end} min={meta?.first_day} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div>
            <label className={t.labelCls}>SK forecast model</label>
            <div className={`${t.inputCls} opacity-80`} title="Walk-forward v50-k (Kumo-Tabular) SK forecast at every decision time.">
              {MODEL_LABEL[params.model] || params.model}
            </div>
          </div>
          <div>
            <label className={t.labelCls} title="MW per quarter and side, hourly and 15-minute products together: an hourly position (Fix I, BASE) counts its MW in each of its four quarters, a 15-minute one (Fix II bucket) only in its own; 1 MW for a quarter = 0.25 MWh. The whole position open after Fixing II: Goose's own IDA1-bound volume plus the extra kept open. Goose volume above the cap is closed at Fixing II instead of IDA1. Empty = no cap.">
              Max open after Fixing II (MW / quarter)
            </label>
            <input type="number" step="5" min="0" className={t.inputCls} value={params.cap_fix2_mw ?? ""} placeholder="no cap" onChange={(e) => setParam("cap_fix2_mw", numOrEmpty(e))} />
          </div>
          <div>
            <label className={t.labelCls} title="MW per quarter and side, hourly and 15-minute products together: an hourly position (Fix I, BASE) counts its MW in each of its four quarters, a 15-minute one (Fix II bucket) only in its own; 1 MW for a quarter = 0.25 MWh. The most that may stay open past IDA1 (towards IDA2 / IDA3 / balancing). Empty = no cap.">
              Max carried past IDA1 (MW / quarter)
            </label>
            <input type="number" step="5" min="0" className={t.inputCls} value={params.cap_ida1_mw ?? ""} placeholder="no cap" onChange={(e) => setParam("cap_ida1_mw", numOrEmpty(e))} />
          </div>
        </div>

        <div className="mb-4">
          <GooseBookPanel
            gooseMeta={meta?.goose}
            goose={goose}
            gooseTotal={sameGoose ? resultBook.goose_total_pln : null}
            dark={dark}
            inputCls={t.inputCls}
            labelCls={t.labelCls}
          />
        </div>

        <PolicyControls params={params} setParam={setParam} ida2Times={meta?.ida2_times} dark={dark} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <FeeControls params={params} setParam={setParam} dark={dark} />
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={runBacktest}
            disabled={loading || !meta || !goose.settings}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Play className="w-4 h-4" />
            {loading ? (goose.status?.state === "computing" || goose.status?.state === "queued" ? "Computing Goose results…" : "Running…") : "Run Backtest"}
          </button>
          <button
            onClick={() => {
              setParams({ ...DEFAULT_PARAMS, ...(meta?.defaults || {}) });
              goose.reset();
            }}
            className={t.btnSecondary}
          >
            Reset to defaults
          </button>
        </div>
      </SectionCard>

      {error ? <div className="rounded-xl border border-rose-300 bg-rose-50 text-rose-700 px-4 py-3 text-sm">{error}</div> : null}
      {result?.notes?.length ? (
        <div className={`rounded-xl border px-4 py-3 text-xs ${dark ? "bg-amber-950/30 border-amber-900 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
          {result.notes.map((n, i) => (
            <div key={i}>{n}</div>
          ))}
        </div>
      ) : null}
      {!result && !loading && !error && meta ? (
        <div className={`text-sm ${t.muted}`}>Choose the dates and press Run Backtest.</div>
      ) : null}

      {/* ---------------- Summary ---------------- */}
      {summary ? (
        <SectionCard title="Summary" icon={<TrendingUp className="w-4 h-4" />} dark={dark}>
          <div className={`flex flex-wrap gap-x-8 gap-y-2 mb-5 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
            <div>
              <span className="font-semibold">{fmtInt(summary.days)}</span> days ({summary.start} → {summary.end}) ·{" "}
              <span className="text-emerald-600 font-semibold">{fmtInt(summary.profitable_days)} profitable</span> /{" "}
              <span className="text-rose-600 font-semibold">{fmtInt(summary.loss_days)} loss</span>
              {Number(summary.pending_days) > 0 ? (
                <span className="text-amber-600 font-semibold"> / {fmtInt(summary.pending_days)} awaiting settlement</span>
              ) : null}
            </div>
            <div>
              vs day-ahead only: <span className="text-emerald-600 font-semibold">{fmtInt(summary.days_better)} better</span> /{" "}
              <span className="text-rose-600 font-semibold">{fmtInt(summary.days_worse)} worse</span> days ·{" "}
              <span className="font-semibold">{fmtInt(summary.months_better)}</span> of {fmtInt(summary.months)} months better
            </div>
            <div>
              Best / worst day vs day-ahead:{" "}
              <span className="text-emerald-600 font-semibold">{fmtSigned(summary.best_day_delta)}</span> ({summary.best_day || "–"}) /{" "}
              <span className="text-rose-600 font-semibold">{fmtSigned(summary.worst_day_delta)}</span> ({summary.worst_day || "–"})
            </div>
            <div title={`Transaction fee ${summary.transaction_fee_rate}% of sales = ${fmtInt(summary.transaction_fee)} PLN; volume fee ${summary.volume_fee_rate} PLN/MWh on ${fmtInt(summary.traded_mwh)} MWh = ${fmtInt(summary.volume_fee)} PLN. Every venue pays both, the balancing market included.`}>
              Fees: <span className="font-semibold text-rose-600">−{fmtInt(summary.total_fees)} PLN</span>
            </div>
          </div>

          <div className="mb-5">
            <SplitSummary split={result.split} gooseTotal={summary.goose_pnl} dark={dark} />
          </div>

          <div className="overflow-x-auto">
            <table className="app-table w-full text-sm">
              <thead>
                <tr className={t.headCls}>
                  <Th left>Metric</Th>
                  <Th>Day-ahead only (Goose)</Th>
                  <Th>Combined (this policy)</Th>
                  <Th>Difference</Th>
                </tr>
              </thead>
              <tbody className={t.bodyCls}>
                {[
                  { l: "P&L (PLN)", g: summary.goose_pnl, c: summary.total_pnl, money: true },
                  { l: "Fees (PLN)", g: summary.goose_fees, c: summary.total_fees },
                  { l: "P&L after fees (PLN)", g: summary.goose_pnl_after_fees, c: summary.pnl_after_fees, money: true },
                  { l: "PLN / MWh", g: summary.goose_pln_per_mwh, c: summary.pln_per_mwh, dec: 2 },
                  { l: "Traded + settled volume (MWh)", g: summary.goose_traded_mwh, c: summary.traded_mwh },
                  { l: "Profitable days", g: summary.goose_profitable_days, c: summary.profitable_days },
                ].map((r) => {
                  const diff = r.g == null || r.c == null ? null : Number(r.c) - Number(r.g);
                  const f = (v) => (r.dec != null ? fmtNum(v, r.dec) : fmtInt(v));
                  return (
                    <tr key={r.l} className={`border-t ${t.rowBorder}`}>
                      <td className={`py-2 px-2 text-xs ${t.muted}`}>{r.l}</td>
                      <td className="py-2 px-2 text-right">{f(r.g)}</td>
                      <td className={`py-2 px-2 text-right font-semibold ${r.money ? signColor(r.c, dark) : ""}`}>{f(r.c)}</td>
                      <td className={`py-2 px-2 text-right ${r.money ? signColor(diff, dark) : ""}`}>
                        {diff == null ? "–" : r.dec != null ? `${diff > 0 ? "+" : ""}${fmtNum(diff, r.dec)}` : fmtSigned(diff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className={`mt-3 text-[11px] ${t.faint}`}>
            Tail check: the two best days carry {fmtPct(summary.top2_share)} of the policy&apos;s added P&amp;L; without them it adds{" "}
            {fmtSigned(summary.delta_ex_top2)} PLN. PLN/MWh uses the Goose position volume ({fmtInt(summary.goose_mwh)} MWh) as the denominator.
          </div>
        </SectionCard>
      ) : null}

      {summary ? <VolumeAndDeferrals report={result} dark={dark} /> : null}
      {summary ? <BreakdownCard report={result} dark={dark} /> : null}
      {summary ? <SkAccuracyCard report={result} model={params.model} dark={dark} /> : null}

      {/* ---------------- Charts ---------------- */}
      {summary ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Cumulative P&L" icon={<TrendingUp className="w-4 h-4" />} dark={dark}>
            <CumChart rows={byDay} dark={dark} />
            <div className={`mt-2 flex justify-between text-[11px] ${t.faint}`}>
              <span>{byDay[0]?.day || ""}</span>
              <span>
                <span className={t.muted}>━ day-ahead only {fmtInt(summary.goose_pnl)}</span>
                {"   "}
                <span className="text-emerald-600 font-semibold">━ combined {fmtInt(summary.total_pnl)} PLN</span>
              </span>
              <span>{byDay.length ? byDay[byDay.length - 1].day : ""}</span>
            </div>
          </SectionCard>
          <SectionCard title="Added P&L by month" icon={<Activity className="w-4 h-4" />} dark={dark}>
            <Bars rows={byMonth} valueKey="delta_pnl" labelKey="month" dark={dark} fmt={fmtSigned} />
          </SectionCard>
        </div>
      ) : null}

      {/* ---------------- Monthly ---------------- */}
      {summary ? (
        <SectionCard title="Monthly breakdown" icon={<CalendarClock className="w-4 h-4" />} dark={dark}>
          <div className="overflow-x-auto">
            <table className="app-table w-full text-sm">
              <thead>
                <tr className={t.headCls}>
                  <Th left>Month</Th>
                  <Th>Day-ahead P&L (PLN)</Th>
                  <Th>Added (PLN)</Th>
                  <Th>Combined P&L (PLN)</Th>
                  <Th>Day-ahead PLN/MWh</Th>
                  <Th>Combined PLN/MWh</Th>
                  <Th>Deferred (MWh)</Th>
                  <Th>Closed IDA2 (MWh)</Th>
                  <Th>Balancing (MWh)</Th>
                  <Th>Balancing days</Th>
                  <Th>Dir. accuracy</Th>
                  <Th>Balancing win %</Th>
                </tr>
              </thead>
              <tbody className={t.bodyCls}>
                {byMonth.map((m) => (
                  <tr key={m.month} className={`border-t ${t.rowBorder}`}>
                    <td className="py-1.5 px-2 text-xs font-medium">{m.month}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.goose_pnl)}</td>
                    <td className={`py-1.5 px-2 text-right font-medium ${signColor(m.delta_pnl, dark)}`}>{fmtSigned(m.delta_pnl)}</td>
                    <td className={`py-1.5 px-2 text-right font-semibold ${signColor(m.total_pnl, dark)}`}>{fmtInt(m.total_pnl)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtNum(m.goose_pln_per_mwh, 2)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtNum(m.pln_per_mwh, 2)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.deferred_mwh)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.ida2_mwh)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.rb_mwh)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(m.rb_days)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtPct(m.dir_acc)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtPct(m.rb_win_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      ) : null}

      {/* ---------------- Daily results ---------------- */}
      {summary ? (
        <SectionCard title="Daily results · from day-ahead to balancing" icon={<Activity className="w-4 h-4" />} dark={dark}>
          <DailyList
            rows={byDay}
            fetchDay={fetchDay}
            i2Label={i2Label}
            dark={dark}
            stats={(d) => (
              <>
                <span>Deferred: <span className="font-medium">{fmtNum(d.deferred_mwh, 0)}</span> MWh</span>
                <span>IDA2: <span className="font-medium">{fmtNum(d.ida2_mwh, 0)}</span></span>
                <span>IDA3: <span className="font-medium">{fmtNum(d.ida3_mwh, 0)}</span></span>
                <span>Balancing: <span className="font-medium">{fmtNum(d.rb_mwh, 0)}</span> MWh</span>
                <span>Day-ahead: <span className="font-medium">{fmtInt(d.goose_pnl)}</span></span>
                <span className={`font-medium ${signColor(d.delta_pnl, dark)}`}>{fmtSigned(d.delta_pnl)}</span>
                <span className={`font-bold text-sm ${signColor(d.total_pnl, dark)}`}>{fmtInt(d.total_pnl)} PLN</span>
              </>
            )}
          />
        </SectionCard>
      ) : null}
    </div>
  );
};

export default DayAheadBacktesting;
